import { AwxCardTransactionLifecycleBillingAmount, WebhookPayload } from '@ankor-io/common/types/awx'
import { CardDeliveryMode } from '@ankor-io/common/types/cards'

export type AWXCardDeliveryAddress = {
  city: string
  country: string
  line1: string
  line2?: string
  postcode: string
  state: string
}

/**
 * AWX card creation request params, @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards_create/post
 */
export type CreateAWXCardRequest = {
  /**
   * Set this to true to activate the physical card when it is created.
   * This will enable use of the card while it’s being delivered to the cardholder.
   * Virtual cards will always be activated upon creation.
   */
  activate_on_issue?: boolean
  authorization_controls: {
    allowed_transaction_count: 'SINGLE' | 'MULTIPLE'
    transaction_limits: {
      /**
       * Currency for transaction limits (3-letter ISO-4217 code). USD will be used if not set.
       * Examples of valid base currencies are USD, AUD, GBP, EUR, CNY, HKD, SGD, NZD, JPY, CAD, CHF.
       */
      currency?: string
      limits: {
        amount: number
        interval: AWXCardLimitsInterval
      }[]
    }
  }
  cardholder_id: string
  /**
   * full legal name of user requesting the new card
   */
  created_by: string
  delivery_details?: {
    preferred_delivery_mode: Omit<CardDeliveryMode, 'UNKNOWN'>
    // Required for EXPRESS delivery or when the destination country is China.
    // We should be OK as our cardholder cannot be created without a mobile number
    // And this does use the cardholders phone number as the fallback when we don't provide it
    mobile_number?: string
  }
  form_factor: 'VIRTUAL' | 'PHYSICAL'
  is_personalized: boolean
  program: {
    purpose: 'COMMERCIAL'
  }
  /**
   * A unique request ID specified by the client. Requests with the same request_id will be ignored.
   * This allows requests to be replayed if client is unsure of the outcome, e.g. due to network issues, system failures, etc.
   * Note: Can be non-UUID as long as it is unique between requests
   */
  request_id: string
  nick_name?: string
  // required only for physical cards, if not provided it will fallback to the cardholder address
  postal_address?: AWXCardDeliveryAddress
}

export enum AWXCardLimitsInterval {
  PER_TRANSACTION = 'PER_TRANSACTION',
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  ALL_TIME = 'ALL_TIME',
}

/**
 * Maximum amount for the mandatory per-transaction limit (Airwallex requirement).
 * This limit is always applied and cannot be changed by the user.
 */
export const PER_TRANSACTION_LIMIT_MAX_AMOUNT = 75000

/**
 * AWX card response, @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards_create/post
 * TODO: add fields as you see fit
 */
export type AWXCardResponse = {
  /**
   * Card brand
   */
  brand: string
  card_id: string
  /**
   * Masked card number (this might be null when the cardholder is not yet active)
   */
  card_number: string | null
  card_status: AWXCardStatus
  card_version: number
  cardholder_id: string
  created_at: string
  created_by: string
  form_factor: 'VIRTUAL' | 'PHYSICAL'
  name_on_card: string
  nick_name: string
}

/**
 * For more info @see https://www.airwallex.com/docs/issuing__create-cards__card-statuses
 */
export enum AWXCardStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE', // frozen
  BLOCKED = 'BLOCKED',
  LOST = 'LOST',
  STOLEN = 'STOLEN',
  CLOSED = 'CLOSED', // cancelled
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
}

/**
 * A single card item as returned by the Airwallex "Get all cards" list endpoint.
 * This list shape is leaner than {@link AWXCardResponse} (get-by-id) and card_status
 * may additionally be UNKNOWN.
 * @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards/get
 */
export type AWXCardListItem = {
  additional_cardholder_ids?: string[]
  /**
   * Card brand
   */
  brand: string
  card_id: string
  /**
   * Masked card number (this might be null when the cardholder is not yet active)
   */
  card_number: string | null
  card_status: AWXCardStatus | 'UNKNOWN'
  /**
   * Present only for employee (cardholder) cards
   */
  cardholder_id?: string
  created_at: string
  nick_name?: string
  updated_at?: string
}

/**
 * Response from the Airwallex "Get all cards" list endpoint.
 * @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards/get
 */
export type ListAWXCardsResponse = {
  has_more: boolean
  items: AWXCardListItem[]
}

/**
 * Query params for the Airwallex "Get all cards" list endpoint.
 * IMPORTANT: the endpoint defaults to a 30-day window unless BOTH from_created_at and
 * to_created_at are provided, and Airwallex caps any single range at 90 days (undocumented).
 * @see https://www.airwallex.com/docs/api#/Issuing/Cards/_api_v1_issuing_cards/get
 */
export type ListAWXCardsParams = {
  card_status?: AWXCardStatus
  cardholder_id?: string
  from_created_at?: string
  to_created_at?: string
  page_num?: number
  page_size?: number
}

/**
 * Request to create a PAN token for secure card details retrieval
 * @see https://www.airwallex.com/docs/issuing__retrieve-sensitive-card-details__secure-iframes
 */
export type CreateAWXPANTokenRequest = {
  card_id: string
}

/**
 * Response from creating a PAN token
 * @see https://www.airwallex.com/docs/issuing__retrieve-sensitive-card-details__secure-iframes
 */
export type AWXPANTokenResponse = {
  /**
   * The PAN token used for secure card details retrieval
   */
  token: string
  /**
   * Token expiration timestamp (tokens expire after 1 minute)
   */
  expires_at: string
}

export type UpdateAWXCardRequest = {
  /**
   * The status to update the card to
   * INACTIVE: The card is frozen
   * CLOSED: The card is cancelled
   * ACTIVE: The card is unfrozen
   */
  card_status?: AWXCardStatus.ACTIVE | AWXCardStatus.INACTIVE | AWXCardStatus.CLOSED
  nick_name?: string
  authorization_controls?: {
    transaction_limits: {
      currency?: string
      limits: {
        amount: number
        interval: AWXCardLimitsInterval
      }[]
    }
    cash_withdrawal_limits: {
      currency?: string
      limits: {
        amount: number
        interval: AWXCardLimitsInterval
      }[]
    }
  }
}

export type UpdateAWXCardStatusRequest = {
  /**
   * Those are the only allowed statuses to update a card to
   * INACTIVE: The card is frozen
   * CLOSED: The card is cancelled
   * ACTIVE: The card is unfrozen
   */
  status: AWXCardStatus.ACTIVE | AWXCardStatus.INACTIVE | AWXCardStatus.CLOSED
}

export type GetCardRemainingLimitsResponse = {
  /**
   * Currency for transaction limits (3-letter ISO-4217 code). USD will be used if not set.
   * Examples of valid base currencies are USD, AUD, GBP, EUR, CNY, HKD, SGD, NZD, JPY, CAD, CHF.
   */
  currency: string
  /**
   * Cash withdrawal limits
   */
  cash_withdrawal_limits: {
    /**
     * Amount of the limit in dollars (can have decimal places)
     */
    amount: number
    /**
     * Interval for the limit
     */
    interval: AWXCardLimitsInterval
    /**
     * Remaining amount of the limit in dollars (can have decimal places)
     */
    remaining: number
  }[]
  /**
   * Transaction limits
   */
  limits: {
    /**
     * Amount of the limit in dollars (can have decimal places)
     */
    amount: number
    /**
     * Interval for the limit
     */
    interval: AWXCardLimitsInterval
    /**
     * Remaining amount of the limit in dollars (can have decimal places)
     */
    remaining: number
  }[]
}

// from https://www.airwallex.com/docs/api#/Issuing/Transactions/_api_v1_issuing_transactions__id_/get
export enum AWXCardTransactionStatus {
  APPROVED = 'APPROVED',
  CLEARED = 'CLEARED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  REVERSED = 'REVERSED',
}

// from https://www.airwallex.com/docs/api#/Issuing/Transactions/_api_v1_issuing_transactions/get
export enum AWXCardTransactionType {
  /**
   * Start legacy values
   */
  AUTHORIZATION = 'AUTHORIZATION',
  CLEARING = 'CLEARING',
  REFUND = 'REFUND',
  REVERSAL = 'REVERSAL',
  ORIGINAL_CREDIT = 'ORIGINAL_CREDIT',
  /**
   * End legacy values
   * -----------------
   * Start new values (same as status)
   */
  APPROVED = 'APPROVED',
  CLEARED = 'CLEARED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  PENDING = 'PENDING',
  REVERSED = 'REVERSED',
}

export type CardTransactionWebhookPayload = WebhookPayload<{
  acquiring_institution_identifier: string
  auth_code: string
  billing_amount: number
  billing_currency: string
  card_id: string
  card_nickname: string
  failure_reason: string | null
  lifecycle_id: string
  masked_card_number: string
  matched_authorizations: string[]
  merchant: {
    category_code: string
    city: string
    country: string
    identifier: string
    name: string
  }
  fee_details: {
    amount: number
    currency: string
    type: string
  }[]
  network_transaction_id: string
  posted_date: string
  retrieval_ref: string
  status: AWXCardTransactionStatus
  transaction_amount: number
  transaction_currency: string
  transaction_date: string
  transaction_id: string
  transaction_type: AWXCardTransactionType
}> & {
  account_id: string
}

export type CardStatusWebhookPayload = WebhookPayload<{
  card_id: string
  card_status: AWXCardStatus
  // Present when the card number becomes available
  card_number?: string
}> & {
  account_id: string
}

export type CardDeliveryUpdateWebhookPayload = WebhookPayload<{
  card_id: string
  delivery_details: {
    updated_at: string
    status: string
    delivery_mode: string
    delivery_vendor: string
    tracking_link?: string
    tracking_number?: string
    status_description?: string
  }
}>

/**
 * New Card Transaction webhook payload types to replace the legacy events.
 * see https://www.airwallex.com/docs/developer-tools/webhooks/listen-for-webhook-events/issuing#card-transaction-status
 */
export enum CardTransactionWebhookEvents {
  // card transaction is authorized
  ISSUING_CARD_TRANSACTION_AUTHORIZED = 'issuing.card_transaction.authorized',
  // card transaction is verified
  ISSUING_CARD_TRANSACTION_VERIFIED = 'issuing.card_transaction.verified',
  // card transaction status changes to cleareed
  ISSUING_CARD_TRANSACTION_CLEARED = 'issuing.card_transaction.cleared',
  // card transaction status changes to reversed
  ISSUING_CARD_TRANSACTION_REVERSED = 'issuing.card_transaction.reversed',
  // card transaction status changes to expired
  ISSUING_CARD_TRANSACTION_EXPIRED = 'issuing.card_transaction.expired',
  // card transaction is declined
  ISSUING_CARD_TRANSACTION_DECLINED = 'issuing.card_transaction.declined',
  // the card transaction was updated without a status change (other fields on the object changed)
  ISSUING_CARD_TRANSACTION_MODIFIED = 'issuing.card_transaction.modified',
}

export enum AWXCardTransactionStatusV2 {
  AUTHORIZED = 'AUTHORIZED',
  CLEARED = 'CLEARED',
  DECLINED = 'DECLINED',
  EXPIRED = 'EXPIRED',
  REVERSED = 'REVERSED',
  VERIFIED = 'VERIFIED',
}

export enum AwxDigitalWalletTokenPaymentMethod {
  APPLE_PAY = 'APPLE_PAY',
  GOOGLE_PAY = 'GOOGLE_PAY',
  CARD_ON_FILE = 'CARD_ON_FILE',
}

export enum FundDirection {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
  NO_MOVEMENT = 'NO_MOVEMENT',
}

export type AwxCardTransaction = {
  // unique identifier of the card transaction
  id: string
  // Unique identifier of the lifecycle this card transaction belongs to. Use this to retrieve all related card transactions (e.g., a purchase and its refund).
  lifecycle_id: string
  // the id of the card the transaction is associated with
  card_id: string
  // ISO 8601 timestamp of when the transaction lifecycle was created
  created_at: string
  // ISO 8601 timestamp of when the transaction lifecycle was last updated (new event received)
  updated_at: string
  // Aggregated billing totals grouped by currency. Each entry contains cumulative amounts across
  // all card transactions and transaction events within this lifecycle, including totals for
  // authorized, debited, credited, declined, expired, pending, and reversed amounts.
  billing_amounts: AwxCardTransactionLifecycleBillingAmount
  digital_wallet_token: {
    id: string
    payment_method: AwxDigitalWalletTokenPaymentMethod
  }
  expiry_date: string
  // only present when status is DECLINED
  failure_reason?: string
  fee_details: {
    amount: number
    currency: string
    type: string
  }[]
  fund_direction: FundDirection
  masked_card_number: string
  merchant: {
    additional_merchant_info: {
      merchant_brand_category_id: number
      merchant_brand_id: number
      merchant_brand_subcategory_id: number
      merchant_logo_url: string
    }
    category_code: string
    city: string
    country: string
    identifier: string
    name: string
    postcode: string
    state: string
  }
  status: AWXCardTransactionStatusV2
  // Aggregated amounts in the merchant's transaction currency (the currency used at the point of sale).
  transaction_amounts: {
    currency: string
    total_authorized: number
    total_credited: number
    total_debited: number
    total_declined: number
    total_expired: number
    total_pending: number
    total_reversed: number
  }
}

export type AWXCardTransactionWebhookPayloadV2 = WebhookPayload<AwxCardTransaction> & {
  account_id: string
}
